import posthog from 'posthog-js';
if (process.env.NODE_ENV !== 'development') {
  posthog.init('phc_kAmsQhGda4UegHubNL0zLsGfivYoajYGqk3Oi2Cq6fl', {
    api_host: 'https://api.hexwood.app',
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
  });
}

/**
 * Analytics tracker for PostHog events.
 * All functions are wrapped in try-catch to prevent crashes from:
 * - Ad blockers blocking the analytics script
 * - Network failures during tracking
 * - Missing/broken posthog library
 */

type HintType = 'manual' | 'automatic';

/**
 * Safely track an event with PostHog.
 * Silently fails if PostHog is not available or tracking fails.
 */
function safeTrack(eventName: string, data: Record<string, any> = {}): void {
  try {
    if (process.env.NODE_ENV !== 'development') {
      posthog.capture(eventName, data);
    }
    console.debug('[Analytics]', eventName, data);
  } catch (error) {
    // Silent fail - never crash the game
    console.debug('[Analytics] Event tracking failed:', eventName, error);
  }
}

/**
 * Identify the current user with PostHog.
 * Call this once on app initialization with the player UUID.
 */
export function identifyPlayer(playerId: string): void {
  try {
    if (process.env.NODE_ENV !== 'development') {
      posthog.identify(playerId);
    }
    console.debug('[Analytics] Identified player:', playerId);
  } catch (error) {
    console.debug('[Analytics] Player identification failed:', error);
  }
}

/**
 * Track when a player starts a level.
 * Event name format: "{level_id}:started" (e.g., "1-1:started")
 */
export function trackLevelStarted(levelId: string, attemptNumber: number): void {
  safeTrack(`level-started`, {
    level_id: levelId,
    attempt_number: attemptNumber,
  });
}

/**
 * Track when a player successfully completes a level.
 */
export function trackLevelFinished(
  levelId: string,
  attemptNumber: number,
  moveCount: number,
  undoCount: number = 0
): void {
  safeTrack(`level-finished`, {
    level_id: levelId,
    attempt_number: attemptNumber,
    num_moves: moveCount,
    undo_count: undoCount,
  });
}

/**
 * Track when a player fails a level (game over).
 * Event name format: "{level_id}:failed" (e.g., "1-1:failed")
 */
export function trackLevelFailed(
  levelId: string,
  attemptNumber: number,
  moveCount: number
): void {
  safeTrack(`level-failed`, {
    level_id: levelId,
    attempt_number: attemptNumber,
    num_moves: moveCount,
  });
}

/**
 * Track when a player explicitly abandons a level (navigates away).
 * Event name format: "{level_id}:abandoned" (e.g., "1-1:abandoned")
 * Note: Only tracks explicit abandonment. Implicit abandonment is inferred from events.
 */
export function trackLevelAbandoned(
  levelId: string,
  attemptNumber: number,
  moveCount: number
): void {
  safeTrack(`level-abandoned`, {
    level_id: levelId,
    attempt_number: attemptNumber,
    num_moves: moveCount,
  });
}

/**
 * Track when a player restarts a level.
 * Event name format: "{level_id}:restarted" (e.g., "1-1:restarted")
 */
export function trackLevelRestarted(
  levelId: string,
  attemptNumber: number,
  moveCountBeforeRestart: number
): void {
  safeTrack(`level-restarted`, {
    level_id: levelId,
    attempt_number: attemptNumber,
    num_moves_before_restart: moveCountBeforeRestart,
  });
}

/**
 * Track when a hint/tutorial is viewed.
 * Event name format: "{level_id}:hint:{type}" (e.g., "1-1:hint:automatic", "1-1:hint:manual")
 * @param hintType - 'manual' for user-initiated, 'automatic' for auto-shown after failures
 */
export function trackHintViewed(
  levelId: string,
  attemptNumber: number,
  hintType: HintType
): void {
  safeTrack(`hint-viewed`, {
    level_id: levelId,
    hint_type: hintType,
    attempt_number: attemptNumber,
  });
}

/**
 * Track when a player checks an enemy's attack range.
 * @param enemyType - Optional archetype type of the enemy
 */
export function trackEnemyRangeChecked(levelId: string, enemyType?: string): void {
  const data: Record<string, any> = {};

  if (enemyType) {
    data.enemy_type = enemyType;
  }
  data.level_id = levelId;

  safeTrack(`enemy-examined`, data);
}

/**
 * Track when a player uses the "Vision" feature to view the optimal solution replay.
 * Vision is a hint that shows the AI's optimal solution as an animated replay.
 */
export function trackVisionViewed(levelId: string, attemptNumber: number): void {
  safeTrack(`vision-viewed`, {
    level_id: levelId,
    attempt_number: attemptNumber,
  });
}

/**
 * Track when a player starts the solution replay player (after winning a level).
 */
export function trackSolutionReplayStarted(levelId: string): void {
  safeTrack(`solution-replay-started`, {
    level_id: levelId,
  });
}

/**
 * Track when a player exits the solution replay player.
 */
export function trackSolutionReplayExited(levelId: string, stepsViewed: number, totalSteps: number): void {
  safeTrack(`solution-replay-exited`, {
    level_id: levelId,
    steps_viewed: stepsViewed,
    total_steps: totalSteps,
  });
}
